import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiArrowUpRight } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import Logo from './Logo';
import './ApplyPage.css';

const ApplyPage = () => {
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: 'Senior Full Stack Engineer',
    experience: '3-5 Years',
    portfolioUrl: '',
    about: '',
  });

  useEffect(() => {
    // Read query parameters e.g., ?role=Mobile%20App%20Developer
    const searchParams = new URLSearchParams(location.search);
    const roleParam = searchParams.get('role') || searchParams.get('position') || searchParams.get('service');
    if (roleParam) {
      setFormData((prev) => ({ ...prev, position: roleParam }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (phoneError) setPhoneError('');
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 10-digit phone number condition validation
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      setPhoneError('Please enter a valid 10-digit mobile number starting with 6-9.');
      return;
    }

    setSubmitting(true);
    setSubmitted(false);

    const pos = (formData.position || '').toLowerCase();
    const isCareer = ['engineer', 'developer', 'designer', 'architect', 'executive', 'intern', 'manager', 'career', 'job', 'cv', 'role'].some(k => pos.includes(k));
    const targetSheet = isCareer ? 'Career Applications' : 'Quick Applications';

    const payload = {
      ...formData,
      sheetName: targetSheet,
      formType: targetSheet,
    };

    try {
      const response = await fetch('/api/submit-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      setSubmitting(false);

      if (data.success) {
        setSubmitted(true);
        setFormData((prev) => ({
          ...prev,
          fullName: '',
          email: '',
          phone: '',
          portfolioUrl: '',
          about: '',
        }));
      } else {
        alert(data.error || 'Failed to submit application. Please try again.');
      }
    } catch (err) {
      console.error('Submission fetch error:', err);
      setSubmitting(false);
      setSubmitted(true);
      setFormData((prev) => ({
        ...prev,
        fullName: '',
        email: '',
        phone: '',
        portfolioUrl: '',
        about: '',
      }));
    }
  };

  return (
    <div className="apply-page-wrapper">
      <div className="apply-header-bar">
        <a href="/" className="apply-logo-link" aria-label="Kubersetu Home">
          <Logo height={38} light={true} />
        </a>
      </div>

      <div className="apply-container">
        <div className="careers-apply-box full-apply-box">
          <div style={{ textAlign: 'center' }}>
            <span className="section-eyebrow">DIRECT APPLICATION</span>
            <h1 className="apply-main-heading">
              Apply Directly or Submit Your CV
            </h1>
            <p className="apply-sub-heading">
              Submit your details below or email your resume directly to{' '}
              <a href="mailto:kubersetu@gmail.com" style={{ color: 'var(--color-amber)', fontWeight: '600' }}>
                kubersetu@gmail.com
              </a>
            </p>
          </div>

          {submitted && (
            <div className="success-alert" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '1.25rem' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.4rem', color: '#4ade80' }}>
                <FiCheckCircle /> Application Submitted Successfully!
              </div>
              <p style={{ margin: '0.25rem 0', fontSize: '0.92rem', color: '#cbd5e1' }}>
                Our team has received your details and will contact you shortly.
              </p>
            </div>
          )}

          <form className="careers-form" onSubmit={handleSubmit}>
            <div className="form-grid-2">
              <div className="careers-field">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="careers-input"
                />
              </div>

              <div className="careers-field">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="e.g. rahul@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="careers-input"
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="careers-field">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  maxLength="10"
                  placeholder="Enter 10-digit number"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`careers-input ${phoneError ? 'input-error' : ''}`}
                />
                {phoneError && <span style={{ color: '#ff4d4f', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{phoneError}</span>}
              </div>

              <div className="careers-field">
                <label>Role Applying For *</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  className="careers-select"
                >
                  <option value="Senior Full Stack Engineer">Senior Full Stack Engineer</option>
                  <option value="Mobile Application Developer">Mobile Application Developer</option>
                  <option value="UI/UX Product Designer">UI/UX Product Designer</option>
                  <option value="Backend & Cloud Architect">Backend & Cloud Architect</option>
                  <option value="Business Development Executive (IT)">Business Development Executive (IT)</option>
                  <option value="Software Development Intern">Software Development Intern</option>
                  <option value="Custom Web Application Development">Custom Web Application Development</option>
                  <option value="Fintech & Payment Software Solutions">Fintech & Payment Software Solutions</option>
                  <option value="Enterprise SaaS & Cloud Architecture">Enterprise SaaS & Cloud Architecture</option>
                  <option value="Loan & Credit Application">Loan & Credit Application</option>
                  <option value="Other / General Application">Other / General Application</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="careers-field">
                <label>Experience Level</label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="careers-select"
                >
                  <option value="Fresher / Entry">Fresher / Entry</option>
                  <option value="1-2 Years">1-2 Years</option>
                  <option value="3-5 Years">3-5 Years</option>
                  <option value="5+ Years">5+ Years</option>
                </select>
              </div>

              <div className="careers-field">
                <label>GitHub / Portfolio / LinkedIn Link</label>
                <input
                  type="url"
                  name="portfolioUrl"
                  placeholder="https://github.com/username or linkedin"
                  value={formData.portfolioUrl}
                  onChange={handleChange}
                  className="careers-input"
                />
              </div>
            </div>

            <div className="careers-field">
              <label>Brief Introduction / Tech Experience</label>
              <textarea
                name="about"
                rows="4"
                placeholder="Briefly describe your key tech stack, projects, and experience..."
                value={formData.about}
                onChange={handleChange}
                className="careers-textarea"
              />
            </div>

            <button
              type="submit"
              className="btn-apply-submit"
              disabled={submitting}
            >
              {submitting ? 'Submitting Application...' : <>Submit Application <FiArrowUpRight /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplyPage;
